import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import withObservables from "@nozbe/with-observables";
import { of } from "rxjs";
import { useAuthContext } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import styles from "./styles.module.css";
import UCashTransactionSuccess from "../../common/UCashTransactionSuccess";
import UCashTransactionReceipt from "../../common/UCashTransactionReceipt";
import DownloadReceipt from "../../common/DownloadReceipt";
import useDownlaodPdf from "../../../hooks/useDownlaodPdf";
import { observeMainInvoicesById } from "../../../database/helpers/invoice";
import { formatNum } from "../../../oscar-pos-core/constants";
import { sentenceCase } from "../../../constants";
import {
  copyReceiptImage,
  dateFormatInNumberWithSpace,
  downloadReceiptImage,
  formattedDateWithSpaceAndTime,
  printWalletReceiptPreview,
} from "../../../utils";

const TransactionSuccessInner = ({ invoiceDetails, customer, id }) => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { business, user } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const { onDownload, downloadLoading } = useDownlaodPdf(user);
  const [showReceipt, setShowReceipt] = useState(false);
  const [isReceiptDownloadLoading, setIsReceiptDownloadLoading] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const receiptRef = useRef();

  let {
    type,
    amount,
    invoice_prefix,
    invoice_number,
    created_at,
    due_date,
    sub_total,
    discount,
    discount_unit,
    tax_rate,
    tax_title,
  } = invoiceDetails || {};
  const isSaleInvoice = type == "in";
  const analyticsPrefix = isSaleInvoice ? "inv" : "pb";

  const handleDownload = async (e) => {
    e?.stopPropagation?.();

    let paramsForReport = {
      template_name: "invoicereport.hbs",
      name: isSaleInvoice ? "Rupin Sale Invoice.pdf" : "Rupin Purchase Order.pdf",
      invoice_id: id,
      template_style: localStorage.getItem("invoiceStyle") || "style1",
      template_color: localStorage.getItem("invoiceColor") || "#000000",
      pdf: true,
    };

    const pdfType = "Invoice";
    const onSuccessDownload = () => {
      triggerLogEvent(analytics, `${analyticsPrefix}_download`);
    };
    onDownload(paramsForReport, onSuccessDownload, navigate, pdfType, business);
  };

  const backRoute = () => {
    const fallback = isSaleInvoice ? "/Sales/SaleInvoice" : "/PurchaseOrders";
    navigate(location?.state?.backPath || fallback, { replace: true });
  };

  useEffect(() => {
    if (invoiceDetails === undefined || invoiceDetails === null) {
      navigate("/Sales/SaleInvoice", { replace: true });
    }
  }, [invoiceDetails]);

  if (!invoiceDetails) return null;

  const invoiceNum = [invoice_prefix, invoice_number].filter(Boolean).join("").replace("#", " #");
  const customerName = customer?.name || "";
  const customerPhone = customer?.phone || "";

  // Discount / tax rows only when applied, with sub total above them (same order as the invoice PDF)
  const hasDiscount = Number(discount) > 0;
  const hasTax = Number(tax_rate) > 0;
  const chargeRows = [
    ...(hasDiscount || hasTax
      ? [{ label: I18n.sub_total, value: `Rs. ${formatNum(sub_total) || 0}` }]
      : []),
    ...(hasDiscount
      ? [{
          label: I18n.discount,
          value: discount_unit === "%" ? `${formatNum(discount)}%` : `Rs. ${formatNum(discount)}`,
        }]
      : []),
    ...(hasTax
      ? [{ label: tax_title ? `${I18n.tax} (${tax_title})` : I18n.tax, value: `${formatNum(tax_rate)}%` }]
      : []),
  ];

  // Same layout as the wallet receipt; rows carry the invoice data
  const receiptRows = [
    { label: I18n.by, value: business?.name || "-" },
    { label: I18n.invoice_no, value: invoiceNum || "-" },
    { label: I18n.date, value: formattedDateWithSpaceAndTime(created_at) || "-" },
    ...(due_date > 0 ? [{ label: I18n.due_date, value: dateFormatInNumberWithSpace(due_date) }] : []),
    ...chargeRows,
    { label: I18n.total_amount, value: `Rs. ${formatNum(amount) || 0}` },
  ];

  const createdWithText = isSaleInvoice ? I18n.invoice_created_with_rupin : I18n.po_created_with_rupin;

  const handleReceiptDownload = () =>
    downloadReceiptImage(receiptRef, {
      setIsDownloadLoading: setIsReceiptDownloadLoading,
      analyticsEvent: `${analyticsPrefix}_receipt_download`,
      fileName: isSaleInvoice ? "Rupin Sale Invoice Receipt" : "Rupin Purchase Order Receipt",
    });

  const handleCopy = () =>
    copyReceiptImage(receiptRef, {
      showSnackbar,
      I18n,
      analyticsEvent: `${analyticsPrefix}_receipt_copy`,
    });

  const handlePrint = () =>
    printWalletReceiptPreview(
      business,
      {
        headerText: createdWithText,
        amount,
        recipientName: customerName,
        bankInfo: customerPhone,
        rows: receiptRows,
      },
      { showSnackbar, setIsPrintLoading, analyticsEvent: `${analyticsPrefix}_receipt_print` },
    );

  return (
    <Fragment>
      {showReceipt ? (
        <UCashTransactionReceipt
          title={sentenceCase(I18n.receipt)}
          amount={amount}
          headerText={createdWithText}
          recipientName={customerName}
          bankInfo={customerPhone}
          rows={receiptRows}
          onBack={() => setShowReceipt(false)}
          onPrint={handlePrint}
          isPrintLoading={isPrintLoading}
          onCopy={handleCopy}
          onDownload={handleReceiptDownload}
          isDownloadLoading={isReceiptDownloadLoading}
        />
      ) : (
        <UCashTransactionSuccess
          subtitle={
            <>
              {customerName ? (
                <span>
                  {isSaleInvoice ? I18n.invoice_to : I18n.purchase_order_to}{" "}
                  <strong>{customerName}</strong>
                </span>
              ) : null}
              {invoiceNum ? <span className={styles.invoiceNum}>{invoiceNum}</span> : null}
            </>
          }
          amount={amount}
          onViewReceipt={() => {
            triggerLogEvent(analytics, `${analyticsPrefix}_view_receipt`);
            setShowReceipt(true);
          }}
          onClose={backRoute}
          primaryBtnTitle={isSaleInvoice ? I18n.download_invoice : I18n.download_po}
          onPrimaryAction={handleDownload}
          isPrimaryLoading={downloadLoading}
        />
      )}

      <DownloadReceipt
        ref={receiptRef}
        amount={amount}
        headerText={createdWithText}
        recipientName={customerName}
        bankInfo={customerPhone}
        rows={receiptRows}
      />
    </Fragment>
  );
};

const withCustomer = withObservables(["invoiceDetails"], ({ invoiceDetails }) => ({
  customer: invoiceDetails?.customer?.observe() ?? of(null),
}));

const TransactionSuccessWithDetails = withCustomer(TransactionSuccessInner);

const enhance = withObservables(["id"], ({ id }) => ({
  invoiceDetails: observeMainInvoicesById(id || ""),
}));

const Enhanced = enhance(({ invoiceDetails, id }) => (
  <TransactionSuccessWithDetails invoiceDetails={invoiceDetails?.[0]} id={id} />
));

export default function TransactionSuccess() {
  const { id } = useParams();
  return <Enhanced id={id} />;
}
